/**
 * API Error Handling Utilities
 * Standardized error handling for API routes
 */

import { NextResponse } from 'next/server'
import { captureException, setContext } from './sentry'

/**
 * Custom API Error Classes
 */
export class APIError extends Error {
  constructor(
    message: string,
    public statusCode: number = 500,
    public code?: string
  ) {
    super(message)
    this.name = 'APIError'
  }
}

export class ValidationError extends APIError {
  constructor(message: string, public details?: Record<string, any>) {
    super(message, 400, 'VALIDATION_ERROR')
    this.name = 'ValidationError'
  }
}

export class UnauthorizedError extends APIError {
  constructor(message: string = 'Unauthorized') {
    super(message, 401, 'UNAUTHORIZED')
    this.name = 'UnauthorizedError'
  }
}

export class ForbiddenError extends APIError {
  constructor(message: string = 'Forbidden') {
    super(message, 403, 'FORBIDDEN')
    this.name = 'ForbiddenError'
  }
}

export class NotFoundError extends APIError {
  constructor(message: string = 'Resource not found') {
    super(message, 404, 'NOT_FOUND')
    this.name = 'NotFoundError'
  }
}

export class RateLimitError extends APIError {
  constructor(message: string = 'Too many requests') {
    super(message, 429, 'RATE_LIMIT_EXCEEDED')
    this.name = 'RateLimitError'
  }
}

export class InternalServerError extends APIError {
  constructor(message: string = 'Internal server error') {
    super(message, 500, 'INTERNAL_SERVER_ERROR')
    this.name = 'InternalServerError'
  }
}

/**
 * Handle API errors and return appropriate response
 */
export function handleAPIError(error: unknown, request?: Request): NextResponse {
  // Log request context if available
  if (request) {
    setContext('request', {
      url: request.url,
      method: request.method,
      headers: Object.fromEntries(request.headers.entries()),
    })
  }

  // Handle known API errors
  if (error instanceof APIError) {
    // Don't capture validation errors, unauthorized, or not found as exceptions
    if (
      error instanceof ValidationError ||
      error instanceof UnauthorizedError ||
      error instanceof NotFoundError
    ) {
      // Just log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.warn(`API Error (${error.statusCode}):`, error.message)
      }

      return NextResponse.json(
        {
          error: error.message,
          code: error.code,
          ...(error instanceof ValidationError && error.details
            ? { details: error.details }
            : {}),
        },
        { status: error.statusCode }
      )
    }

    // Capture other API errors to Sentry
    captureException(error, {
      apiError: true,
      statusCode: error.statusCode,
      code: error.code,
    })

    return NextResponse.json(
      {
        error: error.message,
        code: error.code,
      },
      { status: error.statusCode }
    )
  }

  // Handle standard errors
  if (error instanceof Error) {
    captureException(error, { apiError: true })

    return NextResponse.json(
      {
        error:
          process.env.NODE_ENV === 'production'
            ? 'An unexpected error occurred'
            : error.message,
        code: 'INTERNAL_SERVER_ERROR',
      },
      { status: 500 }
    )
  }

  // Handle unknown errors
  captureException(new Error('Unknown error type'), {
    originalError: error,
    apiError: true,
  })

  return NextResponse.json(
    {
      error: 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
    },
    { status: 500 }
  )
}

/**
 * Wrap API route handlers with error tracking
 */
export function withErrorHandler<T extends (...args: any[]) => Promise<NextResponse>>(
  handler: T
): T {
  return (async (...args: any[]) => {
    try {
      return await handler(...args)
    } catch (error) {
      // Extract request from args if available
      const request = args.find((arg) => arg instanceof Request)
      return handleAPIError(error, request)
    }
  }) as T
}

/**
 * Create a standardized error response
 */
export function createErrorResponse(
  message: string,
  statusCode: number = 500,
  code?: string,
  details?: Record<string, any>
): NextResponse {
  return NextResponse.json(
    {
      error: message,
      ...(code && { code }),
      ...(details && { details }),
    },
    { status: statusCode }
  )
}

/**
 * Create a standardized success response
 */
export function createSuccessResponse<T>(
  data: T,
  statusCode: number = 200
): NextResponse {
  return NextResponse.json(data, { status: statusCode })
}
